/**
 * BeBobi bundle cart: reconciliation + presentation.
 *
 * Loaded from layout/theme.liquid, so it runs on EVERY page. That matters: the
 * cart can be edited from the drawer anywhere on the site, and from /cart,
 * neither of which loads bebobi-spotlight.js (that only ships with its own
 * section). While the reconciler lived there, raising a mat's quantity from the
 * homepage drawer left its components behind.
 *
 * Responsibilities:
 *  1. Reconcile - component quantities are derived from their parent
 *     (parent.quantity * _bbPer, absolute never additive) and orphaned
 *     components are removed. Idempotent, so a redundant pass is a no-op and
 *     the mutate -> cart:update -> mutate loop terminates.
 *  2. Present - lock component quantity controls (the value is derived, so a
 *     manual edit would be silently reverted) and nest component rows under
 *     their parent using the theme's own `cart-items__nested-line`.
 *  3. Gift belt - every eligible mat line (variant metafield
 *     bebobi.gift_belt_sku) earns a free colour-matched belt, quantity equal
 *     to the mat's quantity, kept as a real tracked line (`_bbKind: gift`,
 *     `_bbGiftFor: <mat variant ids>`) that the automatic BXGY discount
 *     zeroes. With a KUKO+ mattress in the bundle the belt follows the
 *     MATTRESS colour instead, falling back to the mat's when no belt exists
 *     in that colour. See the "gift belt" section below.
 *  4. Pack-in components - every mat/bed line whose variant metafield
 *     bebobi.pack_in_components lists components gets them added free
 *     (`_bbKind: packin`, `_bbPackInFor: <mat variant id>`), quantity =
 *     component qty * mat qty. Zeroed by the bebobi-packin-free Shopify
 *     Function, which re-derives entitlement server-side. See the "pack-in
 *     components" section below.
 *
 * Shopify's native equivalents (`item.instructions.can_update_quantity`,
 * `item.parent_relationship`) are populated by the Cart Transform API, which is
 * Plus-only. This is the Basic-plan implementation.
 *
 * Lines are matched by LINE KEY, never variant id: the same variant can be in
 * the cart both as a bundle component and as a standalone purchase, and only
 * the former may be locked or adjusted.
 */
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

export const BB_GROUP = '_bbGroup';
export const BB_ROLE = '_bbRole';
export const BB_SLOT = '_bbSlot';
export const BB_PER = '_bbPer';
export const BB_KIND = '_bbKind';
export const BB_GIFT_FOR = '_bbGiftFor';
const BB_GIFT_KIND = 'gift';
export const BB_PACKIN_FOR = '_bbPackInFor';
const BB_PACKIN_KIND = 'packin';

/* Tag on events we dispatch so we can ignore our own writes. Horizon has no
   distinct cart:add event - CartAddEvent.eventName IS ThemeEvents.cartUpdate -
   so source filtering is required, not optional. */
export const BB_SOURCE = 'bebobi-cart-bundles';
/* Horizon's drawer/cart component. It addresses lines by 1-BASED INDEX
   ({line, quantity}) while we use stable keys ({id, quantity}). Any removal we
   make shifts those indices, so a request it composed before our write would
   land on the wrong line. We therefore let it go quiet before reconciling. */
const BB_DRAWER_SOURCE = 'cart-items-component';
/* Outlasts the drawer's 300ms quantity debounce plus its request. */
const BB_SETTLE_MS = 450;

const EN = (document.documentElement.lang || 'pl').toLowerCase().startsWith('en');
const LOCK_TITLE = EN
  ? 'Quantity follows the product it belongs to'
  : 'Ilość wynika z produktu, do którego należy';

export const bbProp = (line, name) => (line && line.properties ? line.properties[name] : undefined);

const routes = () => {
  const r = (window.Theme && window.Theme.routes) || {};
  return {
    add: r.cart_add_url || '/cart/add.js',
    change: r.cart_change_url || '/cart/change.js',
    cart: (r.cart_url || '/cart') + '.js',
  };
};

async function fetchCart() {
  const res = await fetch(routes().cart, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`cart.js ${res.status}`);
  return res.json();
}

/* ---- reconciler --------------------------------------------------------- */

/** Pure: cart in, operations out. No I/O, so it is trivially testable. */
export function planOps(cart) {
  const lines = (cart && cart.items) || [];
  const groups = new Map();
  for (const line of lines) {
    const g = bbProp(line, BB_GROUP);
    if (!g) continue;                                   // plain purchases: untouched
    if (!groups.has(g)) groups.set(g, { parent: null, components: [] });
    const entry = groups.get(g);
    if (bbProp(line, BB_ROLE) === 'parent') entry.parent = line;
    else if (bbProp(line, BB_ROLE) === 'component') entry.components.push(line);
  }

  const removals = [];
  const updates = [];
  for (const [, entry] of groups) {
    if (!entry.parent) {
      /* orphan sweep: the mat is gone, so its components go too */
      for (const c of entry.components) removals.push({ key: c.key, quantity: 0 });
      continue;
    }
    for (const c of entry.components) {
      const per = parseInt(bbProp(c, BB_PER) || '1', 10) || 1;
      const want = entry.parent.quantity * per;         // absolute target
      if (c.quantity !== want) updates.push({ key: c.key, quantity: want });
    }
  }
  /* removals before updates: an intermediate cart is then only ever incomplete,
     never contradictory (a component with no mat) */
  return [...removals, ...updates];
}

/* ---- stray bundle-tier guard --------------------------------------------
   The "W komplecie" variant tier (option "Zestaw") carries the in-bundle price
   and exists solely for funnel-composed sets. The theme never offers it
   standalone, but a deep link, an old session or a crafted /cart/add can still
   land one in the cart without its mat. Such a line is swapped for its
   "Osobno" sibling (same product, same options otherwise) at the regular
   price; when no sellable sibling exists the line is removed outright —
   losing the line beats selling the bundle discount without the bundle. */
/* PL i EN: opcja "Zestaw" jest tłumaczona na "Set", wartość "W komplecie" na
   "Bundled" — cart.js z /en zwraca przetłumaczone nazwy, matcher musi znać obie */
const TIER_RE = /w\s*komplecie|bundled/i;
const TIER_OPT_RE = /^(zestaw|set)$/i;

function isStrayTier(line) {
  /* a component is legit while its parent mat is in the cart; parentless
     components are already removed by the orphan sweep in planOps */
  if (bbProp(line, BB_ROLE) === 'component' && bbProp(line, BB_GROUP)) return false;
  const opts = line.options_with_values || [];
  if (opts.some((o) => TIER_OPT_RE.test(String(o.name || '').trim()) && TIER_RE.test(o.value || ''))) return true;
  return TIER_RE.test(line.variant_title || '');
}

const productCache = new Map();

async function osobnoSibling(line) {
  if (!line.handle) return null;
  let product = productCache.get(line.handle);
  if (!product) {
    const res = await fetch(`/products/${line.handle}.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    product = await res.json();
    productCache.set(line.handle, product);
  }
  const cur = (product.variants || []).find((v) => v.id === line.variant_id);
  if (!cur || !cur.options.some((o) => TIER_RE.test(o))) return null;
  return (product.variants || []).find((v) =>
    v.id !== cur.id &&
    v.options.length === cur.options.length &&
    v.options.every((val, i) => (TIER_RE.test(cur.options[i]) ? !TIER_RE.test(val) : val === cur.options[i]))
  ) || null;
}

/** Returns true when it changed the cart (the caller then re-reads it). */
async function sanitizeTiers(cart) {
  const strays = ((cart && cart.items) || []).filter(isStrayTier);
  if (!strays.length) return false;
  reconciling = true;
  try {
    for (const line of strays) {
      const sib = await osobnoSibling(line).catch(() => null);
      if (sib && sib.available !== false) {
        const res = await fetch(routes().add, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: sib.id, quantity: line.quantity }] }),
        });
        if (!res.ok) console.error('[bebobi-cart] tier swap add failed', line.key, res.status);
      }
      const res = await fetch(routes().change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: 0 }),
      });
      if (!res.ok) console.error('[bebobi-cart] tier removal failed', line.key, res.status);
    }
  } finally {
    reconciling = false;
  }
  return true;
}

/* ---- gift belt ----------------------------------------------------------
   Data comes from Liquid (snippets/bebobi-gift-data.liquid, rendered on every
   page): window.bbBeltVariants (static, the 12 belt variants: id, qty,
   available) and window.bbGiftMap (variant id -> belt SKU for the variants
   that were in the cart when the page rendered; "" = checked, no gift). A
   variant added during this page's life is not in that map, so the map is
   refreshed through the Section Rendering API (sections/bebobi-gift-map.liquid)
   - only then, and only for the cart's own lines, never the whole catalogue.

   Rules (business, Aga 11 Sep 2026 after order 50110540): EVERY eligible mat
   line earns a gift belt in its own colour, quantity = the mat's quantity;
   mats that map to the same belt share one gift line (quantities summed,
   _bbGiftFor lists their variant ids); NO stock check (since 15 Sep 2026):
   belts are CONTINUE, so an add never fails on stock, and Shopify keeps the
   units of unfulfilled made-to-order orders "committed", which drives
   `available` to 0 while the belts are physically on the shelf - the old
   qty > 0 rule silently dropped gifts for that reason; a belt the
   customer already put in the cart as a purchase blocks the gift of THAT belt
   (the BXGY zeroes their purchased line instead - a gift line on top would be
   a second free belt); a gift line that is not discounted to 0 is removed
   (discount inactive / replaced by a non-combining code) - a "gift" must never
   be charged. */

const GIFT_SECTION = 'bebobi-gift-map';
const REFUSED_KEY = 'bb-gift-refused';

const isGift = (line) => bbProp(line, BB_KIND) === BB_GIFT_KIND;
const giftMap = Object.assign({}, window.bbGiftMap || {});
const beltVariants = () => window.bbBeltVariants || {};

/** Resolve every non-gift cart line in giftMap; false when that was impossible. */
async function ensureGiftMap(cart) {
  const unknown = ((cart && cart.items) || []).filter((l) => !isGift(l) && !(String(l.variant_id) in giftMap));
  if (!unknown.length) return true;
  try {
    const root = window.bbGiftRoot || (routes().cart.replace(/\/cart\.js$/, '') + '/');
    const url = new URL(root, location.origin);
    url.searchParams.set('section_id', GIFT_SECTION);
    const res = await fetch(url, { headers: { Accept: 'text/html' } });
    if (!res.ok) throw new Error(`section ${res.status}`);
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const el = doc.querySelector('[data-bb-gift-map]');
    if (!el) throw new Error('map element missing');
    Object.assign(giftMap, JSON.parse(el.textContent || '{}'));
    for (const l of unknown) if (!(String(l.variant_id) in giftMap)) giftMap[String(l.variant_id)] = '';
    return true;
  } catch (err) {
    console.warn('[bb-gift] map refresh failed, gift step skipped this pass', err);
    return false;
  }
}

/* Belts we must not add again: a 422 on add, or a line that came back
   undiscounted. Scoped to the current set of eligible mat lines AND belts
   bought outright (a purchased belt can take a BXGY allocation away from the
   gift), quantities included (the BXGY applies once per mat unit, so a
   quantity change can make a refused gift discountable again) - as soon as
   the customer changes one of those the set is cleared, so a transient
   refusal never sticks for the whole session. */
const eligibleLines = (cart) => {
  const lines = (cart && cart.items) || [];
  return lines.filter((l) =>
    !isGift(l) &&
    !(bbProp(l, BB_ROLE) === 'component' && bbProp(l, BB_SLOT) === BB_MATTRESS_SLOT) &&
    beltSkuFor(l, lines, giftMap)
  );
};
function giftSignature(cart) {
  const beltIds = new Set(Object.values(beltVariants()).map((b) => b.id));
  const lines = (cart && cart.items) || [];
  const mats = eligibleLines(cart).map((l) => l.variant_id + 'x' + l.quantity);
  const bought = lines.filter((l) => !isGift(l) && beltIds.has(l.variant_id)).map((l) => 'b' + l.variant_id + 'x' + l.quantity);
  return [...mats, ...bought].sort().join(',');
}

function loadRefused(sig) {
  try {
    const raw = JSON.parse(sessionStorage.getItem(REFUSED_KEY) || 'null');
    if (raw && raw.sig === sig && Array.isArray(raw.ids)) return new Set(raw.ids.map(Number));
  } catch (_) { /* storage unavailable: refusals live for this pass only */ }
  return new Set();
}
function refuse(sig, variantId) {
  const set = loadRefused(sig);
  set.add(Number(variantId));
  try { sessionStorage.setItem(REFUSED_KEY, JSON.stringify({ sig, ids: [...set] })); } catch (_) { /* ignore */ }
}

/** Pure: cart + maps in, gift operations out.
    removals: line keys to set to 0; resets: {key, quantity}; adds: /cart/add.js
    items (one POST each, so a 422 is attributable); refuse: belt variant ids
    to remember as refused for this cart signature. */
/* KUKO+ mattress rule (owner, 21 Sep 2026). When the bundle contains a
   mattress, the belt follows the MATTRESS colour; when no belt exists in that
   colour, it falls back to the mat's own belt.

       belt = beltForMattressColour(mattress) || matsOwnBelt

   Expressed as data, not code: the mattress variants carry the same
   bebobi.gift_belt_sku metafield the mats do, so they simply appear in the same
   map. A colour with no belt of its own (Mono brąz, Mono oliwka) is left EMPTY,
   and an empty value falls through to the mat exactly like an absent one.
   Validated against all 60 "With mattress" rows of the matrix sheet.

   Mattresses are KUKO+ only: Dengu's add-on is the rigid floor SZPD23A and Ero
   Soft takes neither, so those families keep the mat's belt by falling through.

   The mattress is found by the bundle grouping the funnel already writes
   (_bbGroup + _bbSlot 'materac'), so nothing new has to be tagged. */
const BB_MATTRESS_SLOT = 'materac';

export function beltSkuFor(line, lines, map) {
  const own = map[String(line.variant_id)];
  const group = bbProp(line, BB_GROUP);
  if (!group || bbProp(line, BB_ROLE) !== 'parent') return own;
  const mattress = lines.find((l) =>
    bbProp(l, BB_GROUP) === group &&
    bbProp(l, BB_ROLE) === 'component' &&
    bbProp(l, BB_SLOT) === BB_MATTRESS_SLOT
  );
  if (!mattress) return own;
  // '' (colour has no belt) and undefined (not a mattress we know) both fall back
  return map[String(mattress.variant_id)] || own;
}

export function planGiftOps(cart, map, belts, refused) {
  const lines = (cart && cart.items) || [];
  const gifts = lines.filter(isGift);
  /* A mattress line carries a belt SKU of its own, but it is a COMPONENT, not a
     mat - it must not earn a belt in its own right, or a bundle would get two.
     Only the parent it belongs to is eligible, and beltSkuFor() reads the
     mattress on its behalf. */
  const eligible = lines.filter((l) =>
    !isGift(l) &&
    !(bbProp(l, BB_ROLE) === 'component' && bbProp(l, BB_SLOT) === BB_MATTRESS_SLOT) &&
    beltSkuFor(l, lines, map)
  );
  const ops = { removals: [], resets: [], adds: [], refuse: [], reason: '' };
  const reasons = [];

  /* desired gift set: one entry per belt variant, quantity = the sum of the
     quantities of the mat lines that map to it */
  const wanted = new Map();                          // belt variant id -> { id, sku, quantity, mats }
  for (const mat of eligible) {
    const sku = beltSkuFor(mat, lines, map);      // mattress colour wins, else the mat's own
    const belt = belts[sku];
    if (!belt) { reasons.push(`belt unknown (${sku})`); continue; }           // no stock check: CONTINUE policy, committed stock is not sold out
    if (refused.has(Number(belt.id))) { reasons.push(`belt refused earlier (${sku})`); continue; }
    const e = wanted.get(belt.id) || { id: belt.id, sku, quantity: 0, mats: [] };
    e.quantity += mat.quantity;
    if (!e.mats.includes(String(mat.variant_id))) e.mats.push(String(mat.variant_id));
    wanted.set(belt.id, e);
  }
  /* already in the cart as a purchase: the BXGY zeroes THAT line, a gift line
     of the same belt on top would be a second free belt */
  for (const [id, e] of wanted) {
    if (lines.some((l) => !isGift(l) && l.variant_id === id)) { wanted.delete(id); reasons.push(`belt already bought (${e.sku})`); }
  }
  if (gifts.length && !eligible.length) reasons.push('no eligible mat left');

  /* reconcile the existing gift entries against the desired set. Shopify
     splits a line whose units are only partly discounted into two cart.js
     entries with DIFFERENT keys (x2 at 0,00 + x1 charged), each addressable
     on its own - so a belt is reconciled across all of its entries: the total
     is brought to the wanted quantity by shrinking charged entries first and
     free ones last; a belt whose total is already right but still has a
     charged entry is genuinely undiscounted and goes (never charge a gift). */
  const entriesByBelt = new Map();
  for (const g of gifts) {
    if (!entriesByBelt.has(g.variant_id)) entriesByBelt.set(g.variant_id, []);
    entriesByBelt.get(g.variant_id).push(g);
  }
  const charged = (g) => g.final_line_price > 0;
  for (const [beltId, entries] of entriesByBelt) {
    const e = wanted.get(beltId);
    if (!e) { for (const g of entries) ops.removals.push(g.key); continue; }     // not wanted any more
    const total = entries.reduce((n, g) => n + g.quantity, 0);
    if (total < e.quantity) {
      const first = entries.find((g) => !charged(g)) || entries[0];             // grow a free entry
      ops.resets.push({ key: first.key, quantity: first.quantity + (e.quantity - total) });
    } else if (total > e.quantity) {
      let excess = total - e.quantity;                                             // shrink charged entries first
      for (const g of [...entries.filter(charged), ...entries.filter((g) => !charged(g))]) {
        if (!excess) break;
        const take = Math.min(g.quantity, excess);
        excess -= take;
        if (take === g.quantity) ops.removals.push(g.key);
        else ops.resets.push({ key: g.key, quantity: g.quantity - take });
      }
    } else if (entries.some(charged)) {
      for (const g of entries) ops.removals.push(g.key);                           // not discounted: never charge for a "gift"
      ops.refuse.push(beltId);
      reasons.push(`gift not discounted, removed (${e.sku})`);
    }
  }
  for (const e of wanted.values()) {
    if (entriesByBelt.has(e.id)) continue;
    ops.adds.push({ id: e.id, quantity: e.quantity, properties: { [BB_KIND]: BB_GIFT_KIND, [BB_GIFT_FOR]: e.mats.join(',') } });
  }
  ops.reason = reasons.join('; ');
  return ops;
}

const giftHasOps = (g) => Boolean(g && (g.removals.length || g.resets.length || g.adds.length));
let lastGiftReason = '';

/** Returns null when the map could not be resolved (no gift writes then). */
async function planGift(cart) {
  if (!(await ensureGiftMap(cart))) return null;
  const sig = giftSignature(cart);
  const ops = planGiftOps(cart, giftMap, beltVariants(), loadRefused(sig));
  for (const id of ops.refuse) refuse(sig, id);
  if (ops.reason && ops.reason !== lastGiftReason) {
    console.log('[bb-gift] ' + ops.reason);
    lastGiftReason = ops.reason;
  }
  return { sig, ops };
}

async function addGift(item, sig) {
  const res = await fetch(routes().add, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [item] }),
  });
  if (!res.ok) {
    /* 422: sold out / DENY / archived - remember for this mat set so the next
       pass does not retry the same add forever */
    console.warn('[bb-gift] belt unavailable', item.id, res.status);
    refuse(sig, item.id);
  }
}

/* ---- pack-in components -------------------------------------------------
   Every mat/bed variant whose metafield bebobi.pack_in_components lists
   components ships with them free: foam inserts, cushions, side battens.
   Data comes from Liquid the same way the gift belt's does
   (snippets/bebobi-packin-data.liquid, rendered on every page):
   window.bbPackInMap (cart variant id -> [{sku, qty}]; [] = checked, no
   components) and window.bbPackInVariants (component SKU -> {id, available},
   because the metafield stores SKUs while /cart/add.js needs variant ids).
   A variant added during this page's life is refreshed through the Section
   Rendering API (sections/bebobi-packin-map.liquid), cart lines only.

   Quantity = component qty * mat qty. That product is the whole reason this
   cannot be a plain automatic discount: SZP50 is 2 per mat for twelve mats
   and 4 per mat for twenty others, and a discount targets variants and
   quantities, never "how many does THIS line entitle".

   UNLIKE THE GIFT BELT in one important way: a component the customer bought
   themselves does NOT suppress the pack-in and is NEVER touched. Someone
   replacing the foam in a bed they already own buys ORTOL at 229,99 zł; that
   line stays charged, and our separate tagged line is what goes free. The
   belt does the opposite (a purchased belt blocks the gift) because a belt is
   a giveaway nobody buys deliberately.

   The 0,00 zł comes from the bebobi-packin-free Shopify Function, not from
   here - nothing client-side can set a line price. Crucially the function
   does NOT trust `_bbKind`: properties are client-settable, so it re-derives
   entitlement from the mats actually in the cart and their own metafields,
   and caps the free quantity at that. A forged line gets charged. This code
   still checks that lines came back at 0 and removes them if not, exactly as
   the belt does - if the function is down, a "free" component must never be
   charged. */

const PACKIN_SECTION = 'bebobi-packin-map';
const REFUSED_PACKIN_KEY = 'bb-packin-refused';

const isPackIn = (line) => bbProp(line, BB_KIND) === BB_PACKIN_KIND;
const packInMap = Object.assign({}, window.bbPackInMap || {});
const packInVariants = () => window.bbPackInVariants || {};

/** Normalise a metafield value: Liquid may hand us JSON or an already-parsed array. */
function parseComponents(raw) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== 'string' || !raw) return [];
  try {
    const p = JSON.parse(raw);
    return Array.isArray(p) ? p : [];
  } catch (_) {
    return [];
  }
}

/** Resolve every non-derived cart line in packInMap; false when impossible. */
async function ensurePackInMap(cart) {
  /* A variant is "resolved" once it appears in the pack-in map; the same
     section also carries the floor map, so one refresh answers both. A floor
     variant added during this page's life is therefore picked up here too -
     it is unknown to packInMap until the refresh, which is what triggers it. */
  const unknown = ((cart && cart.items) || []).filter(
    (l) => !isGift(l) && !isPackIn(l) && !(String(l.variant_id) in packInMap)
  );
  if (!unknown.length) return true;
  try {
    const root = window.bbGiftRoot || (routes().cart.replace(/\/cart\.js$/, '') + '/');
    const url = new URL(root, location.origin);
    url.searchParams.set('section_id', PACKIN_SECTION);
    const res = await fetch(url, { headers: { Accept: 'text/html' } });
    if (!res.ok) throw new Error(`section ${res.status}`);
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const el = doc.querySelector('[data-bb-packin-map]');
    if (!el) throw new Error('map element missing');
    Object.assign(packInMap, JSON.parse(el.textContent || '{}'));
    /* the variant map can grow too: a component product not represented when
       the page rendered (the section ships both) */
    const vel = doc.querySelector('[data-bb-packin-variants]');
    if (vel) {
      try { Object.assign(window.bbPackInVariants || (window.bbPackInVariants = {}), JSON.parse(vel.textContent || '{}')); } catch (_) { /* keep what we have */ }
    }
    /* the same section carries the floor map, so a floor added during this
       page's life is recognised without a second round trip */
    const bel = doc.querySelector('[data-bb-boards-map]');
    if (bel) {
      try { Object.assign(window.bbBoardsPerSet || (window.bbBoardsPerSet = {}), JSON.parse(bel.textContent || '{}')); } catch (_) { /* keep what we have */ }
    }
    for (const l of unknown) if (!(String(l.variant_id) in packInMap)) packInMap[String(l.variant_id)] = [];
    return true;
  } catch (err) {
    console.warn('[bb-packin] map refresh failed, pack-in step skipped this pass', err);
    return false;
  }
}

/* Same refusal discipline as the belt: a 422 on add, or a line that came back
   charged. Scoped to the current set of eligible mats WITH quantities, so any
   change the customer makes clears it and a transient refusal never sticks. */
const packInEligible = (cart) =>
  ((cart && cart.items) || []).filter(
    (l) => !isGift(l) && !isPackIn(l) && parseComponents(packInMap[String(l.variant_id)]).length
  );
function packInSignature(cart) {
  return packInEligible(cart)
    .map((l) => l.variant_id + 'x' + l.quantity)
    .sort()
    .join(',');
}
function loadRefusedPackIn(sig) {
  try {
    const raw = JSON.parse(sessionStorage.getItem(REFUSED_PACKIN_KEY) || 'null');
    if (raw && raw.sig === sig && Array.isArray(raw.ids)) return new Set(raw.ids.map(Number));
  } catch (_) { /* storage unavailable: refusals live for this pass only */ }
  return new Set();
}
function refusePackIn(sig, variantId) {
  const set = loadRefusedPackIn(sig);
  set.add(Number(variantId));
  try { sessionStorage.setItem(REFUSED_PACKIN_KEY, JSON.stringify({ sig, ids: [...set] })); } catch (_) { /* ignore */ }
}

/** Pure: cart + maps in, pack-in operations out. Mirrors planGiftOps.
    Keyed by "<mat variant id>|<component variant id>" so each mat owns its
    own component lines - the same pairing the function uses to check
    entitlement, so the theme can never ask for more than it will get. */
export function planPackInOps(cart, map, variants, refused) {
  const lines = (cart && cart.items) || [];
  const existing = lines.filter(isPackIn);
  const ops = { removals: [], resets: [], adds: [], refuse: [], reason: '' };
  const reasons = [];

  const wanted = new Map();                    // key -> { key, id, sku, mat, quantity }
  for (const mat of lines) {
    if (isGift(mat) || isPackIn(mat)) continue;
    const comps = parseComponents(map[String(mat.variant_id)]);
    for (const c of comps) {
      if (!c || !c.sku) continue;
      const per = parseInt(c.qty, 10);
      if (!(per > 0)) continue;
      const v = variants[String(c.sku).trim().toUpperCase()] || variants[c.sku];
      if (!v || !v.id) { reasons.push(`component unknown (${c.sku})`); continue; }
      if (refused.has(Number(v.id))) { reasons.push(`component refused earlier (${c.sku})`); continue; }
      const key = mat.variant_id + '|' + v.id;
      const e = wanted.get(key) || { key, id: v.id, sku: c.sku, mat: String(mat.variant_id), quantity: 0 };
      e.quantity += per * mat.quantity;        // component qty x mat qty
      wanted.set(key, e);
    }
  }
  if (existing.length && !wanted.size) reasons.push('no eligible mat left');

  /* Group existing lines by the same key. Shopify can split a partly
     discounted line into several entries with different keys, so a pair is
     reconciled across all of its entries: shrink charged entries first, grow
     free ones, and if the total is already right but something is still
     charged the function did not zero it - remove, never charge for a
     component we are calling free. */
  const byKey = new Map();
  for (const l of existing) {
    const k = (bbProp(l, BB_PACKIN_FOR) || '') + '|' + l.variant_id;
    if (!byKey.has(k)) byKey.set(k, []);
    byKey.get(k).push(l);
  }
  const charged = (l) => l.final_line_price > 0;

  for (const [k, entries] of byKey) {
    const e = wanted.get(k);
    if (!e) { for (const l of entries) ops.removals.push(l.key); continue; }   // mat gone, or orphan
    const total = entries.reduce((n, l) => n + l.quantity, 0);
    if (total < e.quantity) {
      const first = entries.find((l) => !charged(l)) || entries[0];
      ops.resets.push({ key: first.key, quantity: first.quantity + (e.quantity - total) });
    } else if (total > e.quantity) {
      let excess = total - e.quantity;
      for (const l of [...entries.filter(charged), ...entries.filter((x) => !charged(x))]) {
        if (!excess) break;
        const take = Math.min(l.quantity, excess);
        excess -= take;
        if (take === l.quantity) ops.removals.push(l.key);
        else ops.resets.push({ key: l.key, quantity: l.quantity - take });
      }
    } else if (entries.some(charged)) {
      for (const l of entries) ops.removals.push(l.key);
      ops.refuse.push(entries[0].variant_id);
      reasons.push(`component not discounted, removed (${e.sku})`);
    }
  }

  for (const e of wanted.values()) {
    if (byKey.has(e.key)) continue;
    ops.adds.push({
      id: e.id,
      quantity: e.quantity,
      properties: { [BB_KIND]: BB_PACKIN_KIND, [BB_PACKIN_FOR]: e.mat },
    });
  }
  ops.reason = reasons.join('; ');
  return ops;
}

const packInHasOps = (p) => Boolean(p && (p.removals.length || p.resets.length || p.adds.length));
let lastPackInReason = '';

/** Returns null when the map could not be resolved (no pack-in writes then). */
async function planPackIn(cart) {
  if (!(await ensurePackInMap(cart))) return null;
  const sig = packInSignature(cart);
  const ops = planPackInOps(cart, packInMap, packInVariants(), loadRefusedPackIn(sig));
  for (const id of ops.refuse) refusePackIn(sig, id);
  if (ops.reason && ops.reason !== lastPackInReason) {
    console.log('[bb-packin] ' + ops.reason);
    lastPackInReason = ops.reason;
  }
  return { sig, ops };
}

async function addPackIn(item, sig) {
  const res = await fetch(routes().add, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [item] }),
  });
  if (!res.ok) {
    /* All 25 components are CONTINUE since 21 Sep 2026, so stock should never
       refuse an add; an archived or deleted component still can. */
    console.warn('[bb-packin] component unavailable', item.id, res.status);
    refusePackIn(sig, item.id);
  }
}

/* ---- rigid floor: a line's quantity is always 3 x sets -------------------
   The warehouse packs 3 boards per rigid floor, so what the customer buys is
   a SET and what the line counts is BOARDS. A floor line therefore always
   sits at boards_per_set x sets. The bebobi-packin-free function zeroes 2 of
   every 3 units, so the money is unchanged: 3 x 30,00 - 60,00 = 30,00.

   THE DELIBERATE EXCEPTION. Everywhere else this file refuses to touch a line
   the customer added themselves - the pack-in reconciler goes out of its way
   to leave a customer's own ORTOL alone. This rule DOES adjust customer lines,
   and that is intended: the customer is buying a set, not a board, so a floor
   line sitting at quantity 1 is a customer holding one third of a floor. The
   exception is deliberately narrow:

     * it applies ONLY to variants carrying bebobi.boards_per_set;
     * it changes QUANTITY only - never price, never removal;
     * it rounds to whole sets and never below 1, so "I want two floors"
       survives, it is not overridden;
     * it applies to both tiers (W komplecie and Osobno), because the
       warehouse packs 3 either way.

   DIRECTIONAL, NOT ROUNDED. Deriving sets as round(quantity / per) looks
   simpler and is wrong: a settled line at 3 that the customer steps up to 4
   rounds straight back to 3, so the + button is dead and they can never buy a
   second set. Instead the previous set count is stored on the line (_bbSets)
   and the observed quantity is compared against it, so a step up means one
   more set and a step down one fewer. From a settled 3, + gives 6.

   _bbSets is stepper state, not authority: the function recomputes what may be
   discounted from the variant's own metafield, so forging _bbSets changes what
   the stepper does next, never what the customer is charged. */

export const BB_SETS = '_bbSets';

const boardsMap = () => window.bbBoardsPerSet || {};
const boardsFor = (line) => {
  const n = parseInt(boardsMap()[String(line.variant_id)], 10);
  return n >= 2 ? n : 0;                       // 0 = not a floor line
};

/** Pure: cart in, floor quantity operations out. */
export function planFloorOps(cart, boards) {
  const lines = (cart && cart.items) || [];
  const ops = [];
  for (const line of lines) {
    const per = parseInt(boards[String(line.variant_id)], 10);
    if (!(per >= 2)) continue;                 // not a floor line

    /* A funnel-composed floor is a bundle COMPONENT: planOps already derives
       its quantity from the mat (parent.quantity * _bbPer, and the funnel sets
       _bbPer to the board count). Two rules driving one line would fight each
       other to the recursion cap, so this one yields - it exists for floors the
       customer added on their own, outside a bundle. */
    if (bbProp(line, BB_ROLE) === 'component' && bbProp(line, BB_GROUP)) continue;

    const stored = parseInt(bbProp(line, BB_SETS) || '', 10);
    let sets;
    if (stored >= 1) {
      /* Known line: the quantity moved relative to where we left it, so read
         the direction of the customer's click. This is the whole point of
         storing the count - see the note above about the dead stepper. */
      if (line.quantity > stored * per) sets = stored + 1;                // pressed +
      else if (line.quantity < stored * per) sets = Math.max(1, stored - 1);  // pressed -
      else sets = stored;                                                 // settled
    } else {
      /* Unknown line - a fresh add, a deep link with ?quantity=, or a cart
         from before this code shipped. There is no previous state to compare
         against, so round UP to the next whole set: the customer asked for at
         least this many boards and we must never silently hand them fewer.
         4 boards therefore becomes 6, not 3. */
      sets = Math.max(1, Math.ceil(line.quantity / per));
    }

    const want = sets * per;
    if (line.quantity === want && stored === sets) continue;    // settled: no write
    ops.push({ key: line.key, quantity: want, sets });
  }
  return ops;
}

/* ---- bundle floor: _bbPer must equal the board count ---------------------
   planFloorOps yields to bundle components, and planOps trusts _bbPer, so a
   funnel floor that entered the cart with the wrong _bbPer (order #1626, 10 Oct
   2026: _bbPer 1, one board shipped instead of three) was never corrected.
   This pass closes that gap and touches nothing else:

     * only bundle COMPONENT lines (role + group) whose variant carries
       bebobi.boards_per_set (window.bbBoardsPerSet, cart-scoped, so it is
       valid here - the cart is exactly what it covers);
     * only when _bbPer differs from the board count;
     * it rewrites _bbPer and sets the quantity to parent.quantity x boards in
       one /cart/change.js write, so planOps then sees a settled line;
     * an orphan (no parent in the group) is left to planOps' orphan sweep.

   Money is unchanged: the bebobi-packin-free function zeroes 2 of every 3
   from the variant's own metafield, so 3 boards still cost one set. */

/** Pure: cart in, _bbPer corrections for bundle floors out. */
export function planFloorPerOps(cart, boards) {
  const lines = (cart && cart.items) || [];
  const parents = new Map();
  for (const line of lines) {
    if (bbProp(line, BB_ROLE) === 'parent' && bbProp(line, BB_GROUP)) parents.set(bbProp(line, BB_GROUP), line);
  }
  const ops = [];
  for (const line of lines) {
    const per = parseInt(boards[String(line.variant_id)], 10);
    if (!(per >= 2)) continue;                                           // not a floor line
    if (!(bbProp(line, BB_ROLE) === 'component' && bbProp(line, BB_GROUP))) continue;  // standalone: planFloorOps
    if (parseInt(bbProp(line, BB_PER) || '', 10) === per) continue;       // already right
    const parent = parents.get(bbProp(line, BB_GROUP));
    if (!parent) continue;                                               // orphan: planOps removes it
    ops.push({ key: line.key, quantity: parent.quantity * per, props: { [BB_PER]: String(per) } });
  }
  return ops;
}

/* /cart/change.js accepts `properties` alongside `quantity`, so the count and
   the stamp are one request (verified against the storefront: the line comes
   back at the new quantity carrying _bbSets). Note it MINTS A NEW LINE KEY
   when properties change, which is why floor ops run before everything else
   in reconcile() - every later op addresses lines by key. */
async function applyFloorOps(ops, cart) {
  const byKey = new Map(((cart && cart.items) || []).map((l) => [l.key, l]));
  for (const op of ops) {
    const line = byKey.get(op.key);
    if (!line) continue;
    const res = await fetch(routes().change, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: op.key,
        quantity: op.quantity,
        properties: { ...(line.properties || {}), ...(op.sets != null ? { [BB_SETS]: String(op.sets) } : {}), ...(op.props || {}) },
      }),
    });
    if (!res.ok) console.error('[bb-floor] quantity change failed', op.key, res.status);
  }
}

let reconciling = false;
let chain = Promise.resolve();
let settleTimer = null;

/** One serialized writer: /cart/change.js handles a single line per request. */
function enqueue(task) {
  chain = chain.then(task, task);
  return chain;
}

async function reconcile(depth = 0) {
  const failedKeys = [];
  let cart = await fetchCart();                 // always fresh, never cached

  /* The floor map arrives with the pack-in map (one section serves both), so
     resolve that first - a floor variant added during this page's life is not
     in window.bbBoardsPerSet until the refresh. Failure is non-fatal: the
     floor pass then simply finds no floor lines this time round. */
  await ensurePackInMap(cart);

  /* Floor quantities settle FIRST and on their own: a properties write MINTS A
     NEW LINE KEY, and every op below addresses lines by key. Re-reading the
     cart afterwards (via the recursive call) is what keeps those keys valid. */
  const floorOps = [...planFloorOps(cart, boardsMap()), ...planFloorPerOps(cart, boardsMap())];
  if (floorOps.length) {
    reconciling = true;
    try { await applyFloorOps(floorOps, cart); } finally { reconciling = false; }
    if (depth < 2) return reconcile(depth + 1);
  }

  const ops = planOps(cart);
  /* gift ops go AFTER the bundle ops: removals first so an intermediate cart
     is never contradictory, resets, and the adds last (they are the only
     writes that create lines) */
  const gift = await planGift(cart);
  if (gift) {
    for (const key of gift.ops.removals) ops.push({ key, quantity: 0 });
    for (const r of gift.ops.resets) ops.push({ key: r.key, quantity: r.quantity });
  }
  const packIn = await planPackIn(cart);
  if (packIn) {
    for (const key of packIn.ops.removals) ops.push({ key, quantity: 0 });
    for (const r of packIn.ops.resets) ops.push({ key: r.key, quantity: r.quantity });
  }
  const giftAdds = gift ? gift.ops.adds : [];
  const packInAdds = packIn ? packIn.ops.adds : [];
  if (!ops.length && !giftAdds.length && !packInAdds.length) { apply(cart); return cart; }
  if (depth >= 2) {
    console.warn('[bebobi-cart] reconcile did not converge, giving up', ops, giftAdds, packInAdds);
    return cart;
  }

  reconciling = true;
  try {
    for (const op of ops) {
      /* id: a stable line key, never a line index. Index addressing is what
         makes concurrent drawer writes retarget the wrong line. */
      if (typeof op.key !== 'string' || !op.key) {
        console.error('[bebobi-cart] refusing to change a line without a key', op);
        continue;
      }
      const res = await fetch(routes().change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: op.key, quantity: op.quantity }),
      });
      if (!res.ok) {
        console.error('[bebobi-cart] cart/change failed', op, res.status);
        if (op.quantity > 0) failedKeys.push(op.key);   // refused raise (e.g. DENY, no stock)
        continue;
      }
      cart = await res.json();
    }
    for (const item of giftAdds) await addGift(item, gift.sig);
    for (const item of packInAdds) await addPackIn(item, packIn.sig);
  } finally {
    reconciling = false;
  }
  if (failedKeys.length) await clampParents(failedKeys);
  return reconcile(depth + 1);
}

/* A component write can be refused (DENY with insufficient stock) while the
   parent already sits at a higher quantity - a drawer raise on the mat. The
   set must never stay contradictory, so the parent is clamped DOWN to what
   its components actually achieved; the next reconcile pass then converges. */
async function clampParents(failedKeys) {
  let cart;
  try { cart = await fetchCart(); } catch (_) { return; }
  const groups = new Map();
  for (const l of (cart && cart.items) || []) {
    const g = bbProp(l, BB_GROUP);
    if (!g) continue;
    if (!groups.has(g)) groups.set(g, { parent: null, components: [] });
    const e = groups.get(g);
    if (bbProp(l, BB_ROLE) === 'parent') e.parent = l;
    else if (bbProp(l, BB_ROLE) === 'component') e.components.push(l);
  }
  reconciling = true;
  try {
    for (const [, e] of groups) {
      if (!e.parent || !e.components.some((c) => failedKeys.includes(c.key))) continue;
      let max = Infinity;
      for (const c of e.components) {
        const per = parseInt(bbProp(c, BB_PER) || '1', 10) || 1;
        max = Math.min(max, Math.floor(c.quantity / per));
      }
      if (!isFinite(max) || max < 1 || e.parent.quantity <= max) continue;
      const res = await fetch(routes().change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: e.parent.key, quantity: max }),
      });
      if (!res.ok) console.error('[bebobi-cart] parent clamp failed', e.parent.key, res.status);
    }
  } finally {
    reconciling = false;
  }
}

async function reconcileIfNeeded() {
  try {
    const before = await fetchCart();
    const swapped = await sanitizeTiers(before);
    const gift = swapped ? null : await planGift(before);
    const packIn = swapped ? null : await planPackIn(before);
    const floor = swapped ? [] : [...planFloorOps(before, boardsMap()), ...planFloorPerOps(before, boardsMap())];
    if (!swapped && !planOps(before).length && !giftHasOps(gift && gift.ops) && !packInHasOps(packIn && packIn.ops) && !floor.length) { apply(before); return; }
    const cart = await reconcile();
    apply(cart);
    /* Tell the theme, so the drawer and cart page redraw with the new
       quantities. Without this the numbers are correct on the server but stale
       on screen until the next interaction. */
    document.dispatchEvent(new CartUpdateEvent(cart, BB_SOURCE, {
      source: BB_SOURCE,
      itemCount: cart.item_count,
    }));
  } catch (err) {
    console.error('[bebobi-cart] reconcile failed', err);
  }
}

/* ---- presentation ------------------------------------------------------- */

/** component line key -> parent line key */
function classify(cart) {
  const lines = (cart && cart.items) || [];
  const parentByGroup = new Map();
  for (const l of lines) {
    if (bbProp(l, BB_ROLE) === 'parent') parentByGroup.set(bbProp(l, BB_GROUP), l.key);
  }
  const components = new Map();
  for (const l of lines) {
    if (bbProp(l, BB_ROLE) !== 'component') continue;
    const parentKey = parentByGroup.get(bbProp(l, BB_GROUP));
    if (parentKey) components.set(l.key, parentKey);
  }
  return components;
}

function apply(cart) {
  const components = classify(cart);
  const giftKeys = new Set(((cart && cart.items) || []).filter(isGift).map((l) => l.key));
  const packInKeys = new Set(((cart && cart.items) || []).filter(isPackIn).map((l) => l.key));

  for (const row of document.querySelectorAll('[data-key]')) {
    const key = row.dataset.key;
    if (!key) continue;
    const parentKey = components.get(key);
    const isComponent = Boolean(parentKey);
    const isGiftRow = giftKeys.has(key);
    const isPackInRow = packInKeys.has(key);
    /* gift and pack-in rows: Liquid already renders the badge and hides the
       controls (snippets/cart-products.liquid); this is the fallback between a
       cart write and the section morph */
    const locked = isComponent || isGiftRow || isPackInRow;

    row.classList.toggle('cart-items__nested-line', isComponent);
    row.classList.toggle('bb-component-line', isComponent);
    row.classList.toggle('bb-gift-line', isGiftRow);
    row.classList.toggle('bb-packin-line', isPackInRow);
    if (isComponent) row.setAttribute('data-bb-parent-key', parentKey);
    else row.removeAttribute('data-bb-parent-key');

    /* the cart renders <cart-quantity-selector-component>, the product page
       renders <quantity-selector-component> - handle both */
    const selector = row.querySelector('cart-quantity-selector-component, quantity-selector-component');
    if (!selector) continue;
    selector.classList.toggle('bb-locked', locked);
    if (isComponent) selector.setAttribute('title', LOCK_TITLE);
    else selector.removeAttribute('title');

    for (const control of selector.querySelectorAll('button, input')) {
      if (locked) {
        if (!control.hasAttribute('disabled')) {
          control.setAttribute('disabled', '');
          control.setAttribute('data-bb-disabled', '');   // ours to undo, theirs to keep
        }
      } else if (control.hasAttribute('data-bb-disabled')) {
        control.removeAttribute('disabled');
        control.removeAttribute('data-bb-disabled');
      }
    }
  }
}

async function refresh() {
  try { apply(await fetchCart()); } catch (_) { /* presentation only */ }
}

/* ---- wiring ------------------------------------------------------------- */

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (reconciling) return;
  const d = (event && event.detail) || {};
  const source = d.data && d.data.source;
  if (source === BB_SOURCE) return;                      // our own write

  if (source === BB_DRAWER_SOURCE) {
    /* let the drawer's in-flight requests land before we touch anything */
    clearTimeout(settleTimer);
    settleTimer = setTimeout(() => enqueue(reconcileIfNeeded), BB_SETTLE_MS);
    return;
  }
  enqueue(reconcileIfNeeded);
});

/* The drawer's markup is already in the DOM and merely revealed on open, so
   watching for added nodes is not enough. Watch for rows changing at all
   (morphSection replaces them) and debounce - our own class writes would
   otherwise re-trigger the observer. */
let pending = null;
const scheduleRefresh = () => { clearTimeout(pending); pending = setTimeout(refresh, 60); };

new MutationObserver((records) => {
  for (const r of records) {
    if (r.type === 'attributes') continue;               // ignore apply()'s own writes
    if (r.addedNodes.length || r.removedNodes.length) { scheduleRefresh(); return; }
  }
}).observe(document.documentElement, { childList: true, subtree: true });

document.addEventListener('click', (e) => {
  if (e.target.closest?.('button, a[href$="/cart"], cart-drawer-component')) scheduleRefresh();
}, true);

/* A `defer` module executes AFTER DOMContentLoaded, so a {once:true} listener
   for it would never fire. Reconcile immediately: this also repairs a cart left
   inconsistent by an earlier session. */
enqueue(reconcileIfNeeded);
